import { defineStore as defineQuasarStore } from '#q-app';
import { createPinia, PiniaPluginContext } from 'pinia';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { useEnvsStore } from 'stores/envs';
import { useAuthStore } from './auth';

/*
 * When adding new properties to stores, you should also
 * extend the `PiniaCustomProperties` interface.
 * @see https://pinia.vuejs.org/core-concepts/plugins.html#typing-new-store-properties
 */
declare module 'pinia' {
  export interface PiniaCustomProperties {
    api: AxiosInstance;
  }
}

/*
 * If not building with SSR mode, you can
 * directly export the Store instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Store instance.
 */

export const apiPlugin = (context: PiniaPluginContext) => {
  let api = context.app.config.globalProperties.$api;

  if (!api) {
    const $envsStore = useEnvsStore();
    $envsStore.loadEnvs();

    api = axios.create({
      baseURL: $envsStore.API_BASE,
      validateStatus: function (status) {
        return (status >= 200 && status < 300) || status == 400;
      },
    });

    const authStore = useAuthStore();
    api.interceptors.request.use((config: AxiosRequestConfig) => {
      config.withCredentials = true;

      return config;
    });

    api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if ([401].includes(<number>error.response?.status)) {
          authStore.redirectToLogin();
        }
        return Promise.reject(error);
      }
    );
  }
  context.store.api = api;
};

export default defineQuasarStore((/* { ssrContext } */) => {
  const pinia = createPinia();

  pinia.use(apiPlugin);

  return pinia;
});
